import getConfig from 'next/config'
import { useCallback, useMemo, useState } from 'react'
import * as Yup from 'yup'

import { ampli } from '@Analytics/ampli'
import { identify } from '@Analytics/identify'
import { useSubscribeToNewsletterMutation } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

type SubscribeDataFormat = {
  campaignLabel?: string
  email: string
  name?: string
  source?: string
}

type UseNewsletterSubscriptionReturn = {
  checkEmailSuggestions: (email: string, setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void) => Promise<boolean>
  isSuggestion: boolean
  loading: boolean
  newsletterSignupSchema: Yup.ObjectSchema<any>
  subscribe: (data: SubscribeDataFormat) => Promise<boolean>
}

export const useNewsletterSubscription = (formikRef?: React.MutableRefObject<any>): UseNewsletterSubscriptionReturn => {
  const [performSubscribeQuery] = useSubscribeToNewsletterMutation()
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation('validation')

  const [lastSubmittedValue, setLastSubmittedValue] = useState('')
  const [isSuggestion, setIsSuggestion] = useState(false)
  const { publicRuntimeConfig } = getConfig()

  const subscribe = useCallback(async ({ campaignLabel = '', email, source = '' }: SubscribeDataFormat) => {
    setLoading(true)

    try {
      const result = await performSubscribeQuery({ variables: { campaign: campaignLabel, email, source } })
      const isSuccess = result.data?.subscribeToNewsletter || false

      if (!isSuccess) {
        setLoading(false)
        return false
      }

      await identify({ email })

      await ampli.newsletterOptIn({
        campaign_label: campaignLabel,
        source_id: source,
      })

      setLoading(false)
      return true
    } catch {
      setLoading(false)
      return false
    }
  }, [performSubscribeQuery])

  const checkEmailSuggestions = useCallback(async (
    value: string,
  ) => {
    if (lastSubmittedValue === value) {
      return false
    }

    setLoading(true)

    // Check for email auto-suggestion:
    const raw = await fetch(`${publicRuntimeConfig.apiEndpoint}/v2/communication/emailValidation`, {
      method: 'post',
      body: JSON.stringify({ email: value, source: 'success' }),
    })

    const res = await raw.json()

    // Suggestion recieved, autocorrect the email before opt-in
    const hasSuggestion = !!res.suggestion
    if (hasSuggestion) {
      const email = value.split('@')[0]
      const emailSuggestion = `${email}@${res.suggestion}`
      formikRef?.current.setFieldValue('email', emailSuggestion)
    }

    setLastSubmittedValue(value || '')
    setIsSuggestion(hasSuggestion)
    setLoading(false)

    return hasSuggestion
  }, [formikRef, lastSubmittedValue, publicRuntimeConfig.apiEndpoint])

  const newsletterSignupSchema = useMemo(() => Yup.object().shape({
    email: Yup.string()
      .email(t('YOUR_EMAIL_IS_INVALID'))
      .required(t('EMAIL_REQUIRED_TEXT'))
      .test({
        async test(value) {
          await checkEmailSuggestions(value || '')

          return true
        },
      }),
  }), [t, checkEmailSuggestions])

  return {
    isSuggestion,
    loading,
    subscribe,
    checkEmailSuggestions,
    newsletterSignupSchema,
  }
}
